// ActionItemForm - Form for creating/editing action items (US-2.3)
// Allows facilitator to add owner and due date to action items

import React, { useState } from 'react';
import styles from './ActionItemForm.module.css';

interface ActionItemFormProps {
  onSubmit: (data: { text: string; owner?: string; dueDate?: string }) => void;
  onCancel: () => void;
  initialData?: {
    text?: string;
    owner?: string;
    dueDate?: string;
  };
  mode: 'create' | 'edit';
}

const ActionItemForm: React.FC<ActionItemFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  mode,
}) => {
  const [text, setText] = useState(initialData?.text || '');
  const [owner, setOwner] = useState(initialData?.owner || '');
  const [dueDate, setDueDate] = useState(initialData?.dueDate || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // VALIDATE text is not empty
    if (text.trim().length === 0) {
      return;
    }

    // SUBMIT form data
    onSubmit({
      text: text.trim(),
      owner: owner.trim() || undefined,
      dueDate: dueDate || undefined,
    });

    // RESET form
    setText('');
    setOwner('');
    setDueDate('');
  };

  const remainingChars = 250 - text.length;
  const isTextValid = text.trim().length > 0 && text.length <= 250;
  const isOwnerValid = owner.length <= 100;

  return (
    <div className={styles.actionItemFormOverlay}>
      <form className={styles.actionItemForm} onSubmit={handleSubmit}>
        <h3>{mode === 'create' ? 'Action Item erstellen' : 'Action Item bearbeiten'}</h3>

        <div className={styles.formGroup}>
          <label htmlFor="action-text">
            Action Item <span className={styles.required}>*</span>
          </label>
          <textarea
            id="action-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Was muss getan werden?"
            maxLength={250}
            rows={3}
            required
            autoFocus
          />
          <div className={`${styles.charCounter} ${remainingChars < 20 ? styles.warning : ''}`}>
            {remainingChars} Zeichen übrig
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="action-owner">Verantwortlich (optional)</label>
          <input
            id="action-owner"
            type="text"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Wer übernimmt das?"
            maxLength={100}
          />
          {owner.length > 90 && (
            <div className={styles.charCounter}>{100 - owner.length} Zeichen übrig</div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="action-due-date">Fällig am (optional)</label>
          <input
            id="action-due-date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={onCancel}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            className={styles.btnSubmit}
            disabled={!isTextValid || !isOwnerValid}
          >
            {mode === 'create' ? 'Erstellen' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ActionItemForm;
